/**
 * Avisos de validación de los atributos HTML, en el idioma de la app.
 *
 * Un `<form>` sin `noValidate` deja que el navegador compruebe `required`,
 * `minLength`, `type="email"`, `type="number"` con `min`/`max`/paso o
 * `pattern` ANTES de que llegue el `onSubmit`. Si algo falla, cancela el envío
 * y pinta su propia burbuja, con la tipografía del sistema y en el idioma del
 * ORDENADOR, no en el que el usuario eligió en la app. El 12 oct 2026 un
 * usuario que sólo lee inglés recibió en el alta «Incluye un signo "@" en la
 * dirección de correo electrónico» (arreglado en `src/features/auth/` por #19).
 *
 * Por eso todo `<form>` lleva `noValidate` (lo exige ESLint). Pero
 * `noValidate` sólo apaga la burbuja y el bloqueo automático: el motor sigue
 * ahí. `element.validity` se calcula igual, con las mismas reglas de `email`,
 * el mismo «`minLength` sólo si el usuario editó» y el `badInput` de un número
 * a medio teclear (`1e`), que es lo único que no llega a React: su `value` es
 * `''`. Este módulo lee ese estado y lo cuenta con las palabras de la app.
 *
 * Uso, como PRIMERA instrucción del `submit` (el navegador también validaba
 * antes del `onSubmit`):
 *
 *   const invalido = firstInvalidControl(e.currentTarget, t);
 *   if (invalido) { toast({ tone: 'error', title: invalido.message }); return; }
 *
 * Los formularios con `react-hook-form` + zod no lo necesitan: sus mensajes
 * pasan por `translateValidationMessage` en `FormField`.
 *
 * Sin imports ni React a propósito: `nativeValidityMessage` es pura y la
 * prueba `scripts/validation-language-check.mjs` en Node.
 */

/** La firma de `useT()`. */
export type Translate = (key: string, vars?: Record<string, string | number>) => string;

/** Lo que se lee de `ValidityState`; todo opcional para poder probarlo sin DOM. */
export interface ValidityLike {
  valueMissing?: boolean;
  typeMismatch?: boolean;
  badInput?: boolean;
  patternMismatch?: boolean;
  tooLong?: boolean;
  tooShort?: boolean;
  rangeUnderflow?: boolean;
  rangeOverflow?: boolean;
  stepMismatch?: boolean;
  customError?: boolean;
}

/** Los atributos del control que hacen falta para redactar el aviso. */
export interface ControlLike {
  type?: string;
  min?: string;
  max?: string;
  minLength?: number;
  maxLength?: number;
  step?: string;
  validationMessage?: string;
}

/**
 * Puro: qué decir para un estado de validez.
 *
 * Mismo orden de prioridad que los navegadores. Lo que no se reconoce cae en
 * `validation.invalid`, nunca en el texto del navegador.
 */
export function nativeValidityMessage(v: ValidityLike, c: ControlLike, t: Translate): string {
  if (v.valueMissing) return t('validation.required');
  if (v.typeMismatch) {
    if (c.type === 'email') return t('validation.emailInvalid');
    if (c.type === 'url') return t('validation.urlInvalid');
    return t('validation.invalid');
  }
  if (v.badInput) {
    return c.type === 'number' ? t('validation.number') : t('validation.invalid');
  }
  if (v.patternMismatch) return t('validation.pattern');
  if (v.tooLong) return t('validation.maxLength', { n: c.maxLength ?? '' });
  if (v.tooShort) return t('validation.minLength', { n: c.minLength ?? '' });
  if (v.rangeUnderflow) return t('validation.min', { min: c.min ?? '' });
  if (v.rangeOverflow) return t('validation.max', { max: c.max ?? '' });
  if (v.stepMismatch) {
    // Sin `step` un número va de 1 en 1: el fallo es un decimal.
    const step = (c.step ?? '').trim();
    return step === '' || step === '1' ? t('validation.wholeNumber') : t('validation.invalid');
  }
  // `setCustomValidity` lo puso la app, así que ya viene en su idioma.
  if (v.customError) return c.validationMessage || t('validation.invalid');
  return t('validation.invalid');
}

/** Un control que participa en la validación nativa (input, select, textarea…). */
interface ConstraintControl extends HTMLElement {
  readonly willValidate: boolean;
  readonly validity: ValidityState;
  readonly validationMessage: string;
}

function isConstraintControl(el: Element): el is ConstraintControl {
  return el instanceof HTMLElement && 'willValidate' in el && 'validity' in el;
}

/**
 * DOM: el primer control inválido del formulario, en orden del árbol. Lo
 * enfoca y devuelve el aviso; `null` si todo es válido.
 *
 * No llama a `checkValidity()` (dispara eventos `invalid`) ni a
 * `reportValidity()` (pinta la burbuja): sólo lee `validity`.
 */
export function firstInvalidControl(
  form: HTMLFormElement,
  t: Translate,
): { element: HTMLElement; message: string } | null {
  for (const el of Array.from(form.elements)) {
    if (!isConstraintControl(el) || !el.willValidate || el.validity.valid) continue;
    const attrs = el as Partial<HTMLInputElement>;
    const message = nativeValidityMessage(
      el.validity,
      {
        type: attrs.type,
        min: attrs.min,
        max: attrs.max,
        minLength: attrs.minLength,
        maxLength: attrs.maxLength,
        step: attrs.step,
        validationMessage: el.validationMessage,
      },
      t,
    );
    el.focus();
    return { element: el, message };
  }
  return null;
}
